import { useCallback, useEffect, useRef, useState } from "react";
import { CurrencyInput } from "@/components/shared/currency-input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { currentFiscalYear } from "@/lib/fiscal";
import { CopyToFYSelect, SectionHeader, savedFieldClass, useSaveFlash } from "./shared";

export function CampusBudgetSection({
  ownerId,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const [annualBudget, setAnnualBudget] = useState("");
  const [loading, setLoading] = useState(true);
  const { savedField, flashSaved } = useSaveFlash();
  const lastSavedRef = useRef("");

  const fetchBudget = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/campus-budget?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        const val = data.budget?.annualBudget != null ? String(data.budget.annualBudget) : "";
        setAnnualBudget(val);
        lastSavedRef.current = val;
        onDataChange(val !== "");
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  useEffect(() => {
    fetchBudget();
  }, [fetchBudget]);

  const saveBudget = useCallback(async () => {
    if (annualBudget === lastSavedRef.current) return;

    const res = await fetch(`/api/owners/${ownerId}/campus-budget`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fiscalYear,
        annualBudget: annualBudget === "" ? null : Number(annualBudget),
      }),
    });

    if (res.ok) {
      const data = await res.json();
      const val = data.budget?.annualBudget != null ? String(data.budget.annualBudget) : "";
      lastSavedRef.current = val;
      flashSaved("annualBudget");
      onDataChange(val !== "");
    }
  }, [ownerId, fiscalYear, annualBudget, flashSaved, onDataChange]);

  const monthlyAllocation =
    annualBudget !== "" && !Number.isNaN(Number(annualBudget))
      ? Math.round(Number(annualBudget) / 12)
      : null;

  async function removeAll() {
    await fetch(`/api/owners/${ownerId}/campus-budget`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, annualBudget: null }),
    });
    setAnnualBudget("");
    lastSavedRef.current = "";
    onDataChange(false);
    onRemove?.();
  }

  async function copyToFY(targetFY: number) {
    if (annualBudget === "") return;
    await fetch(`/api/owners/${ownerId}/campus-budget`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fiscalYear: targetFY,
        annualBudget: Number(annualBudget),
      }),
    });
  }

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="Campus Budget" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && annualBudget !== "" && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <div className={loading ? "opacity-60" : ""}>
          <div className="space-y-3">
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1.5">
                <Label className="text-sm">Annual Campus Budget</Label>
                <CurrencyInput
                  id="campus-annual-budget"
                  value={annualBudget}
                  onChange={setAnnualBudget}
                  onBlur={saveBudget}
                  placeholder="e.g. $40,000"
                  disabled={readOnly}
                  className={savedFieldClass(savedField, "annualBudget")}
                />
              </div>
            </div>
            {monthlyAllocation !== null && (
              <p className="text-sm text-muted-foreground">
                Monthly Allocation:{" "}
                <span className="font-semibold text-foreground">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(monthlyAllocation)}
                  /mo
                </span>
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Travel, Meals, Systems, prizes, ad spend, campus activations, etc.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
